import React, { useState } from "react";
import { NSCA_ROWS, roundTo, fmt, toNum, rpeForPct } from "../lib/calc";
import { exportAthleteChartPDF } from "../lib/pdf";
import { Plus, Trash2, ChevronRight, Users, FileDown, Link2 } from "lucide-react";
import { buildShareUrl, copyToClipboard } from "../lib/share";
import { toast } from "sonner";

const DEFAULT_EXERCISES = ["Squat", "Développé couché", "Soulevé de terre", "Développé militaire", "Épaulé"];

const SEED_ATHLETES = [
  {
    id: "ath-1",
    name: "Marc-Antoine Tremblay",
    category: "Hockey Junior Majeur",
    records: { "Squat": 365, "Développé couché": 255, "Soulevé de terre": 455, "Développé militaire": 165, "Épaulé": 225 },
  },
  {
    id: "ath-2",
    name: "Émilie Bouchard",
    category: "Rugby Élite",
    records: { "Squat": 245, "Développé couché": 145, "Soulevé de terre": 315, "Développé militaire": 95, "Épaulé": 155 },
  },
  {
    id: "ath-3",
    name: "Félix Roy",
    category: "Football Universitaire",
    records: { "Squat": 425, "Développé couché": 315, "Soulevé de terre": 515, "Développé militaire": 195, "Épaulé": 275 },
  },
  {
    id: "ath-4",
    name: "Camille Lavoie",
    category: "Cross-Training",
    records: { "Squat": 225, "Développé couché": 135, "Soulevé de terre": 285, "Développé militaire": 90, "Épaulé": 140 },
  },
];

export const useAthletes = () => {
  const [athletes, setAthletes] = useState(() => {
    try {
      const stored = localStorage.getItem("kraken_athletes_v1");
      if (stored) return JSON.parse(stored);
    } catch (e) {
      /* ignore */
    }
    return SEED_ATHLETES;
  });

  const persist = (next) => {
    setAthletes(next);
    try {
      localStorage.setItem("kraken_athletes_v1", JSON.stringify(next));
    } catch (e) {
      /* ignore */
    }
  };

  return [athletes, persist];
};

export const CoachDashboard = ({
  unit,
  rounding,
  athletes,
  setAthletes,
  onLoadIntoCalculator,
}) => {
  const [selected, setSelected] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState("Squat");

  const addAthlete = () => {
    const empty = DEFAULT_EXERCISES.reduce((a, e) => ({ ...a, [e]: 0 }), {});
    const nouveau = {
      id: `ath-${Date.now()}`,
      name: "Nouvel athlète",
      category: "Général",
      records: empty,
    };
    setAthletes([...athletes, nouveau]);
    setSelected(nouveau.id);
  };

  const removeAthlete = (id) => {
    setAthletes(athletes.filter((a) => a.id !== id));
    if (selected === id) setSelected(null);
  };

  const updateAthlete = (id, patch) => {
    setAthletes(
      athletes.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    );
  };

  const updateRecord = (id, exercise, value) => {
    setAthletes(
      athletes.map((a) =>
        a.id === id
          ? {
              ...a,
              records: {
                ...a.records,
                [exercise]: value === "" ? "" : Number(value),
              },
            }
          : a,
      ),
    );
  };

  const activeAthlete = athletes.find((a) => a.id === selected);
  const activeRM = toNum(activeAthlete?.records?.[selectedExercise]);

  return (
    <div className="tactical-card p-5 sm:p-6 animate-fade" data-testid="coach-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <Users size={12} /> Mode coach · Équipe
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase text-white leading-none mt-1">
            Matrice multi-athlètes
          </h2>
          <p className="text-xs text-slate-400 mt-1.5">
            Édite les 1RM de chaque athlète pour chaque exercice. Aucun compte
            requis : les noms et les 1RM sont enregistrés uniquement dans ce
            navigateur (localStorage) et ne sont envoyés à aucun serveur. Seules
            des statistiques d'usage anonymes sont collectées, et les liens
            partagés ne contiennent aucun nom d'athlète.
          </p>
        </div>
        <button
          data-testid="button-add-athlete"
          onClick={addAthlete}
          className="btn-red px-3 py-2 rounded-sm text-xs flex items-center gap-2"
        >
          <Plus size={14} /> Ajouter athlète
        </button>
      </div>

      <div className="overflow-x-auto -mx-2">
        <table
          data-testid="athlete-table"
          className="ph-no-capture w-full min-w-[900px] border-collapse"
        >
          <thead>
            <tr className="text-left border-b border-white/10">
              <th className="eyebrow py-2 px-2">Athlète</th>
              {DEFAULT_EXERCISES.map((e) => (
                <th key={e} className="eyebrow py-2 px-2 text-right">
                  {e} ({unit})
                </th>
              ))}
              <th className="eyebrow py-2 px-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {athletes.map((a) => (
              <tr
                key={a.id}
                data-testid={`athlete-row-${a.id}`}
                className={`border-b border-white/5 transition ${
                  selected === a.id
                    ? "bg-[#cd383d]/[0.06]"
                    : "hover:bg-white/[0.02]"
                }`}
              >
                <td className="py-2 px-2">
                  <input
                    value={a.name}
                    onChange={(e) => updateAthlete(a.id, { name: e.target.value })}
                    className="bg-transparent border-b border-transparent hover:border-white/20 focus:border-[#cd383d] outline-none text-white font-semibold text-sm w-full"
                  />
                  <input
                    value={a.category}
                    onChange={(e) =>
                      updateAthlete(a.id, { category: e.target.value })
                    }
                    className="bg-transparent border-b border-transparent hover:border-white/10 focus:border-white/40 outline-none text-slate-500 text-[11px] font-mono uppercase tracking-widest w-full mt-0.5"
                  />
                </td>
                {DEFAULT_EXERCISES.map((ex) => (
                  <td key={ex} className="py-2 px-1 text-right">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={a.records[ex] ?? ""}
                      onChange={(e) => updateRecord(a.id, ex, e.target.value)}
                      className="w-20 bg-[#12141a] border border-white/10 rounded-sm px-2 py-1.5 text-right font-mono text-sm text-white focus:border-[#cd383d] outline-none tabular"
                    />
                  </td>
                ))}
                <td className="py-2 px-2 text-right whitespace-nowrap">
                  <button
                    data-testid={`open-athlete-${a.id}`}
                    onClick={() => setSelected(a.id)}
                    className="btn-ghost px-2 py-1 rounded-sm text-[11px] mr-1"
                  >
                    Voir
                  </button>
                  <button
                    data-testid={`remove-athlete-${a.id}`}
                    onClick={() => removeAthlete(a.id)}
                    className="btn-ghost px-2 py-1 rounded-sm text-[11px] text-slate-400 hover:text-[#cd383d]"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {activeAthlete && (
        <div
          className="ph-no-capture mt-6 border-t border-white/10 pt-5 animate-fade"
          data-testid="athlete-drawer"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="eyebrow">Vue rapide</div>
              <h3 className="font-display text-xl font-extrabold uppercase text-white mt-1">
                {activeAthlete.name}
              </h3>
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
                {activeAthlete.category}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                data-testid="drawer-exercise-select"
                value={selectedExercise}
                onChange={(e) => setSelectedExercise(e.target.value)}
                className="bg-[#12141a] border border-white/10 rounded-sm px-3 py-2 text-xs text-white focus:border-[#cd383d] outline-none"
              >
                {DEFAULT_EXERCISES.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
              <button
                data-testid="drawer-share-link"
                onClick={async () => {
                  const url = buildShareUrl({
                    exercise: selectedExercise,
                    oneRM: activeRM,
                    unit,
                    rounding,
                    pct: 85,
                  });
                  const ok = await copyToClipboard(url);
                  toast[ok ? "success" : "error"](
                    ok ? "Lien de la fiche copié" : "Copie impossible",
                    {
                      description: ok
                        ? "Le lien contient l'exercice et le 1RM, aucun nom d'athlète."
                        : "Copie le lien manuellement.",
                    },
                  );
                }}
                className="btn-ghost px-3 py-2 rounded-sm text-xs flex items-center gap-2"
              >
                <Link2 size={14} /> Lien athlète
              </button>
              <button
                data-testid="drawer-export-pdf"
                onClick={() =>
                  exportAthleteChartPDF({
                    athlete: activeAthlete,
                    exercises: DEFAULT_EXERCISES,
                    exercise: selectedExercise,
                    unit,
                    rounding,
                  })
                }
                className="btn-ghost px-3 py-2 rounded-sm text-xs flex items-center gap-2"
              >
                <FileDown size={14} /> Fiche PDF athlète
              </button>
              <button
                data-testid="drawer-load-into-calc"
                onClick={() =>
                  onLoadIntoCalculator(activeRM, selectedExercise)
                }
                className="btn-red px-3 py-2 rounded-sm text-xs flex items-center gap-2"
              >
                Charger dans le calcul <ChevronRight size={14} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-11 gap-2">
            {NSCA_ROWS.map((row) => {
              const load = roundTo((activeRM * row.pct) / 100, rounding);
              return (
                <div
                  key={row.pct}
                  className="rounded-sm bg-black/25 border border-white/10 p-2 text-center"
                >
                  <div
                    className="font-mono text-xs font-black"
                    style={{ color: row.color }}
                  >
                    {row.pct}%
                  </div>
                  <div className="font-mono text-white font-bold text-sm mt-0.5 tabular">
                    {fmt(load, rounding ? 0 : 1)}
                  </div>
                  <div className="text-[9px] text-slate-500 font-mono uppercase tracking-widest">
                    {row.reps} RM
                  </div>
                  <div className="text-[9px] text-[#cd383d]/80 font-mono uppercase tracking-widest">
                    RPE {rpeForPct(row.pct).rpe}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export { DEFAULT_EXERCISES };
